import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

interface RevenuePieChartProps {
  data?: {
    summary?: {
      Won_Revenue?: number;
      Lost_Revenue?: number;
      Sent_Revenue?: number;
      Draft_Revenue?: number;
    };
  };
}

const RevenuePieChart: React.FC<RevenuePieChartProps> = ({ data }) => {
  // Prepare data for pie chart with consistent colors from QuotationBreakdown
  const pieData = [
    {
      name: "Won",
      value: data?.summary?.Won_Revenue || 0,
      color: "rgb(2, 219, 45)", // Bootstrap success color (same as QuotationBreakdown)
    },
    {
      name: "Sent",
      value: data?.summary?.Sent_Revenue || 0,
      // color: "#ffc107", // Bootstrap warning color (same as QuotationBreakdown)
      color: "rgb(255, 170, 0)", // Bootstrap warning color (same as QuotationBreakdown)
    },
    {
      name: "Lost",
      value: data?.summary?.Lost_Revenue || 0,
      color: "#fd5e53", // Bootstrap danger color (same as QuotationBreakdown)
    },
    {
      name: "Draft",
      value: data?.summary?.Draft_Revenue || 0,
      color: "#005D97", // Bootstrap primary color (same as QuotationBreakdown)
    },
  ].filter(item => item.value > 0); // Only show segments with revenue

  const formatCurrency = (value: number) => {
    return `₹ ${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded shadow-lg border">
          <p className="fw-bold mb-1">{payload[0].name}</p>
          <p className="text-primary mb-0">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  const renderLabel = (entry: any) => {
    if (entry.value > 0) {
      return `${entry.name}`;
    }
    return null;
  };

  return (
    <div className="card h-100 shadow-sm border-0" style={{ outline: 'none' }}>
      <div className="card-header bg-white border-0 pb-0">
        <div className="d-flex align-items-center">

          {/* Revenue Icon */}
          <div
            className="bg-primary bg-opacity-10 rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
            style={{ width: "40px", height: "40px" }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              className="text-primary"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                fill="currentColor"
              />
            </svg>
          </div>

          {/* Revenue Title */}
          <div>
            <h6 className="mb-1 fw-bold text-dark">Revenue Breakdown</h6>
            <p className="text-muted mb-0 small">
              Distribution by <span className="text-primary">current quotation status</span>
            </p>
          </div>
        </div>
      </div>

      {/* Revenue Pie Chart */}
      <div className="card-body">
        {/* Revenue Pie Chart Container */}
        {pieData.length > 0 ? (
          <div 
            style={{ width: "100%", height: "300px" }}
            className="revenue-pie-chart-container"
          >
            <style>
              {`
                /* Remove all focus outlines and borders from Recharts elements */
                .recharts-wrapper,
                .recharts-wrapper *,
                .recharts-pie,
                .recharts-pie *,
                .recharts-sector,
                .recharts-sector *,
                .recharts-legend-item,
                .recharts-legend-item *,
                .recharts-legend-wrapper,
                .recharts-legend-wrapper * {
                  outline: none !important;
                  border: none !important;
                  box-shadow: none !important;
                }
                
                .recharts-wrapper:focus,
                .recharts-wrapper:focus *,
                .recharts-pie:focus,
                .recharts-pie:focus *,
                .recharts-sector:focus,
                .recharts-sector:focus *,
                .recharts-sector:hover,
                .recharts-sector:hover *,
                .recharts-sector:active,
                .recharts-sector:active *,
                .recharts-legend-item:focus,
                .recharts-legend-item:focus *,
                .recharts-legend-item:hover,
                .recharts-legend-item:hover *,
                .recharts-legend-item:active,
                .recharts-legend-item:active * {
                  outline: none !important;
                  border: none !important;
                  box-shadow: none !important;
                }
                
                /* Target SVG elements specifically */
                svg,
                svg *,
                svg:focus,
                svg:focus *,
                svg:hover,
                svg:hover *,
                svg:active,
                svg:active * {
                  outline: none !important;
                  border: none !important;
                  box-shadow: none !important;
                }
                
                /* Remove any default browser focus styles */
                *:focus {
                  outline: none !important;
                  border: none !important;
                  box-shadow: none !important;
                }
                
                /* Additional specific targeting for the container */
                .revenue-pie-chart-container,
                .revenue-pie-chart-container *,
                .revenue-pie-chart-container:focus,
                .revenue-pie-chart-container:focus * {
                  outline: none !important;
                  border: none !important;
                  box-shadow: none !important;
                }
                
                /* Disable focus on all interactive elements within the chart */
                .revenue-pie-chart-container * {
                  -webkit-tap-highlight-color: transparent !important;
                  -webkit-touch-callout: none !important;
                  -webkit-user-select: none !important;
                  -khtml-user-select: none !important;
                  -moz-user-select: none !important;
                  -ms-user-select: none !important;
                  user-select: none !important;
                }
              `}
            </style>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart tabIndex={-1}>
                {/* Revenue Pie Chart */}
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderLabel}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value, entry: any) => (
                    <span style={{ color: entry.color }}>
                      {value}: {formatCurrency(entry.payload.value)}
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100">
            {/* No Revenue Data */}
            <div className="text-center">
              <div className="text-muted mb-2">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-muted"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M12 6v6l4 2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <p className="text-muted mb-0">No revenue data available</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenuePieChart;
